import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useEffect } from "react";
import { I18nextProvider } from "react-i18next";

import { useCurrentUser } from "~/api/queries/useCurrentUser";
import { USER_ROLE } from "~/config/userRoles";
import { useGlobalVideoUploadNotifications } from "~/hooks/useGlobalVideoUploadNotifications";
import { useVideoUploadResumeStore } from "~/modules/common/store/useVideoUploadResumeStore";

import i18n from "../../../i18n";
import { queryClient } from "../../api/queryClient";
import { LanguageProvider } from "../Dashboard/Settings/Language/LanguageProvider";
import { ThemeProvider } from "../Theme";

/**
 * Las notificaciones de subida de video sólo le sirven a quien sube videos.
 *
 * Vivía como un `useGlobalVideoUploadNotifications()` pelado en `Providers`, o sea en
 * TODAS las rutas: la home pública abría un WebSocket y se traía `socket.io-client` para
 * un visitante anónimo que no puede subir nada.
 *
 * Va en un componente aparte y no en `Providers` porque `useCurrentUser` necesita estar
 * DENTRO del `QueryClientProvider`, que `Providers` recién monta en su return.
 */
function VideoUploadNotifications() {
  const { data: currentUser } = useCurrentUser();
  const role = currentUser?.role;
  useGlobalVideoUploadNotifications(role === USER_ROLE.admin || role === USER_ROLE.contentCreator);
  return null;
}

export function Providers({ children }: { children: React.ReactNode }) {
  const { pruneExpiredUploads } = useVideoUploadResumeStore();

  useEffect(() => {
    pruneExpiredUploads();
  }, [pruneExpiredUploads]);

  return (
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        {/* PostHog ya no se monta acá: lo carga `lib/tracking` con `import()` y sólo con key
            configurada y consentimiento de analítica. Antes viajaba en el chunk raíz de todas
            las páginas (~58% del chunk) aunque estuviera apagado. */}
        {/* Sin TourProvider: los tours de onboarding del fork no tenían pasos ni ruta que
              los disparara, y montaban @reactour/tour en toda la app. */}
        <VideoUploadNotifications />
        <ThemeProvider>
          <LanguageProvider>{children}</LanguageProvider>
          {process.env.NODE_ENV === "development" && <ReactQueryDevtools initialIsOpen={false} />}
        </ThemeProvider>
      </QueryClientProvider>
    </I18nextProvider>
  );
}
